<template>
  <v-dialog v-model="dialog" persistent width="500">
    <!-- PHASE 1 – Dados pessoais -->
    <v-card v-if="phase === 1">
      <v-card-title class="d-flex align-center">
        <div class="d-flex flex-column">
          <div v-if="form.name" class="text-caption grey--text">
            <v-icon small class="mr-1">mdi-account</v-icon>
            {{ form.name }}
          </div>
          <div>Passo 1 – Dados pessoais</div>
        </div>

        <v-spacer />
        <v-btn icon @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text>
        <v-text-field
          v-model="form.name"
          :color="insurer.color"
          label="Nome completo"
          outlined
          dense
        />

        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="form.contact.contact1"
              label="Contato principal"
              :color="insurer.color"
              outlined
              dense
              v-mask="'### ### ###'"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              v-model="form.contact.contact2"
              label="Contato secundário"
              :color="insurer.color"
              outlined
              dense
              v-mask="'### ### ###'"
            />
          </v-col>
        </v-row>

        <v-text-field
          v-model="form.contact.email"
          label="Email"
          :color="insurer.color"
          outlined
          dense
        />
      </v-card-text>

      <v-card-actions>
        <v-btn block :color="insurer.color" dark @click="nextAfterEdit(2)">
          Continuar
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- PHASE 2 – Endereço -->
    <v-card v-if="phase === 2">
      <v-card-title class="d-flex align-center">
        <div>Passo 2 – Endereço</div>
        <v-spacer />
        <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-card-text>
        <AddressFormStep
          ref="addressStep"
          :init="form.contact.address"
          :theme-color="insurer.color"
        />
      </v-card-text>

      <v-card-actions>
        <v-btn
          block
          :color="insurer.color"
          dark
          @click="saveAddressAndContinue"
        >
          Continuar
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- PHASE 3 – Tipologia -->
    <v-card v-if="phase === 3">
      <v-card-title class="d-flex align-center">
        <div>Passo 3 – Tipologia</div>
        <v-spacer />
        <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-card-text>
        <RiskLocationTypologyStep
          v-model="form.risk_location_typology"
          :key="form.id"
          :insurer="insurer"
        />
      </v-card-text>

      <v-card-actions>
        <v-btn block :color="insurer.color" dark @click="nextAfterEdit(4)">
          Continuar
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- PHASE 4 – Confirmação -->
    <v-card v-if="phase === 4">
      <v-card-title class="d-flex align-center">
        <div>Adicionar lesado</div>
        <v-spacer />
        <v-btn icon @click="close"><v-icon>mdi-close</v-icon></v-btn>
      </v-card-title>

      <v-card-text>
        <div class="mb-4">Confirme os dados do lesado antes de continuar.</div>
        <!-- Dados pessoais -->
        <ConfirmationItem
          label="Nome"
          :value="form.name"
          @edit="handleEditName"
          :insurer="insurer"
        />

        <ConfirmationItem
          label="Contacto principal"
          :value="form.contact.contact1"
          @edit="handleEditContact1"
          :insurer="insurer"
        />

        <ConfirmationItem
          label="Contacto secundário"
          :value="form.contact.contact2"
          @edit="handleEditContact2"
          :insurer="insurer"
        />

        <ConfirmationItem
          label="Email"
          :value="form.contact.email"
          @edit="handleEditEmail"
          :insurer="insurer"
        />
        <ConfirmationItem
          label="Morada"
          :value="addressResume"
          @edit="editFromConfirmation(2)"
          :insurer="insurer"
        />
        <ConfirmationItem
          label="Tipologia"
          :value="typologyLabel"
          @edit="editFromConfirmation(3)"
          :insurer="insurer"
        />
      </v-card-text>

      <v-card-actions>
        <v-btn block :color="insurer.color" dark @click="confirm">
          Confirmar
        </v-btn>
      </v-card-actions>

      <!-- Dialogs -->
      <TextFieldDialog
        ref="nameDialog"
        title="Alterar nome"
        fieldLabel="Nome completo"
        @submit="form.name = $event"
        :color="insurer.color"
        dark
      />

      <TextFieldDialog
        ref="contact1Dialog"
        title="Alterar contacto principal"
        fieldLabel="Contacto"
        @submit="form.contact.contact1 = $event"
        :color="insurer.color"
        dark
        placeholder="999 999 999"
        mask="### ### ###"
      />

      <TextFieldDialog
        ref="contact2Dialog"
        title="Alterar contacto secundário"
        fieldLabel="Contacto secundário"
        optional
        @submit="form.contact.contact2 = $event"
        :color="insurer.color"
        dark
        placeholder="999 999 999"
        mask="### ### ###"
      />

      <TextFieldDialog
        ref="emailDialog"
        title="Alterar email"
        fieldLabel="Email"
        @submit="form.contact.email = $event"
        :color="insurer.color"
        dark
      />
    </v-card>
  </v-dialog>
</template>
<script>
import AddressFormStep from "./AddressFormStep.vue";
import RiskLocationTypologyStep from "./RiskLocationTypologyStep.vue";
import TextFieldDialog from "../dialogs/TextFieldDialog.vue";
import ConfirmationItem from "./ConfirmationItem.vue";

export default {
  name: "CreateParticipantDialog",

  components: {
    AddressFormStep,
    RiskLocationTypologyStep,
    ConfirmationItem,
    TextFieldDialog,
  },

  props: {
    insurer: Object,
  },

  data() {
    return {
      dialog: false,
      phase: 1,
      isEdit: false,
      returnToConfirmation: false,
      form: this.emptyForm(),
    };
  },

  methods: {
    emptyForm() {
      return {
        id: null,
        name: "",
        participant_type: {
          canonical: "third_party",
          description: "Terceiro",
        },
        contact: {
          contact1: "",
          contact2: "",
          email: "",
          address: null,
        },
        risk_location_typology: null,
      };
    },

    open(participant = null) {
      this.phase = 1;
      this.isEdit = !!participant;

      const base = participant
        ? JSON.parse(JSON.stringify(participant))
        : this.emptyForm();

      this.form = {
        ...this.emptyForm(),
        ...base,
      };

      if (!this.form.id) {
        this.form.id = crypto.randomUUID();
      }

      if (!this.isEdit && !this.form.contact.address) {
        const defaultAddress = this.getDefaultAddressFromInsured();

        if (defaultAddress) {
          this.form.contact.address = defaultAddress;
        }
      }

      this.dialog = true;
    },

    close() {
      this.dialog = false;
    },

    editFromConfirmation(targetPhase) {
      this.returnToConfirmation = true;
      this.phase = targetPhase;
    },

    nextAfterEdit(defaultNext) {
      this.phase = this.returnToConfirmation ? 4 : defaultNext;
      this.returnToConfirmation = false;
    },

    saveAddressAndContinue() {
      if (this.$refs.addressStep) {
        this.form.contact.address = this.$refs.addressStep.getValue();
      }
      this.nextAfterEdit(3);
    },

    confirm() {
      this.$emit(this.isEdit ? "update" : "create", { ...this.form });
      this.close();
    },

    handleEditName() {
      this.$refs.nameDialog.open(this.form.name);
    },

    handleEditContact1() {
      this.$refs.contact1Dialog.open(this.form.contact.contact1);
    },

    handleEditContact2() {
      this.$refs.contact2Dialog.open(this.form.contact.contact2);
    },

    handleEditEmail() {
      this.$refs.emailDialog.open(this.form.contact.email);
    },

    getDefaultAddressFromInsured() {
      try {
        const insured = JSON.parse(localStorage.getItem("insured"));

        if (!insured?.contracts?.[0]?.address) {
          return null;
        }

        const address = JSON.parse(
          JSON.stringify(insured.contracts[0].address)
        );

        address.number = null;

        return address;
      } catch (e) {
        return null;
      }
    },
  },

  computed: {
    addressResume() {
      const a = this.form.contact.address;
      if (!a) return "—";

      return [
        a.street,
        a.number,
        a.city?.name,
        a.country_area?.name,
        a.postal_code,
      ]
        .filter(Boolean)
        .join(", ");
    },

    typologyLabel() {
      const map = {
        house: "Moradia",
        apartment: "Apartamento",
        garage: "Garagem",
        office: "Escritório",
        commercial_room: "Espaço comercial",
        warehouse: "Armazém",
        land: "Terreno",
        building: "Prédio",
      };

      return map[this.form.risk_location_typology] || "—";
    },
  },
};
</script>
